import React, { useContext as useContextCore, useState } from 'react'
import filter from 'lodash/filter'
import find from 'lodash/find'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import union from 'lodash/union'
import xor from 'lodash/xor'
import type { Blog } from '@ztechapps/common.contract'
import type { BlogCategoryNode, FlatBlogCategory } from '../../BlogCategory/blogCategoryTree.utils'
import type { BlogCategoryRecord } from '../../BlogCategory/types'
import type { UseBlogCategoryDragDropResult } from './useBlogCategoryDragDrop'
import type { UseBlogCategoryTreeActionsResult } from './useBlogCategoryTreeActions'
import { blogPermissions, getReservedBlogCategoryIds, isReservedBlogCategoryId } from '@ztechapps/blog.common'
import { useRole, useUser } from '@ztechapps/common.hook'
import { countBlogsByBlogCategory } from '../../BlogCategory/blogCategoryTree.utils'
import { useBlogCategoryTree } from '../../BlogCategory/useBlogCategoryTree'
import { useBlogCategoryDragDrop } from './useBlogCategoryDragDrop'
import { useBlogCategoryTreeActions } from './useBlogCategoryTreeActions'

export type BlogCategoryTreeViewContextType = UseBlogCategoryDragDropResult &
  UseBlogCategoryTreeActionsResult & {
    tree: BlogCategoryNode
    flat: FlatBlogCategory[]
    selectedBlogCategory?: FlatBlogCategory
    selectedBlogs: Blog[]
    expandedIds: string[]
    /** Whether the user may move categories and blogs. */
    canEdit: boolean
    /** Blogs filed directly in a blog category, not in the blog categories beneath it. */
    countBlogsIn: (categoryId: string) => number
    /** Blogs filed directly in a blog category whatever the toolbar filter hides; Delete is offered only when it is 0. */
    countAllBlogsIn: (categoryId: string) => number
    hasChildren: (categoryId: string) => boolean
    isReserved: (categoryId: string) => boolean
    /** Whether the reserved What's New blog category is the selected one. */
    isWhatsNewSelected: boolean
    selectBlogCategory: (categoryId: string) => void
    toggleExpanded: (categoryId: string) => void
  }

const context = React.createContext<BlogCategoryTreeViewContextType>({} as BlogCategoryTreeViewContextType)
const CoreProvider = context.Provider

export type BlogCategoryTreeViewProviderProps = {
  categories: BlogCategoryRecord[]
  /** The blogs the toolbar search, filter and sort leave: the blog table and the displayed counts. */
  blogs: Blog[]
  /** Every blog, unfiltered: the delete rule counts these. */
  allBlogs: Pick<Blog, 'category'>[]
  /** The blog categories carry no tenant id (Payroll): the reserved ones are the tenant-free pair. */
  tenantFree?: boolean
  children: React.ReactNode
}

export const BlogCategoryTreeViewProvider = ({
  categories,
  blogs,
  allBlogs,
  tenantFree = false,
  children,
}: BlogCategoryTreeViewProviderProps) => {
  const { tree, flat } = useBlogCategoryTree({ categories })
  const { hasRole: canEdit } = useRole({ roleName: blogPermissions.canEdit.value })
  const { user } = useUser()
  const actions = useBlogCategoryTreeActions()

  const hasChildren = (categoryId: string) => find(flat, { _id: categoryId })?._hasChildren ?? false

  // Opens on the blog category of the first (latest) blog, with every branch expanded. The provider mounts once the lists have
  // loaded, so these initial values see the real records.
  const [selectedId, setSelectedId] = useState(first(allBlogs)?.category?._id)
  const [expandedIds, setExpandedIds] = useState(() =>
    map(
      filter(flat, (category) => hasChildren(category._id)),
      '_id'
    )
  )
  const expand = (categoryId: string) => setExpandedIds((current) => union(current, [categoryId]))

  const moveBlog = actions.moveBlog
  const moveBlogCategory = async (categoryId: string, parentId: string | null) => {
    const result = await actions.moveBlogCategory(categoryId, parentId)
    // The destination opens so the moved blog category shows beneath it.
    if (parentId && isEmpty(result?.errors)) expand(parentId)
    return result
  }
  const addBlogCategory = async (name: string, parentId: string | null) => {
    const result = await actions.addBlogCategory(name, parentId)
    if (parentId && isEmpty(result?.errors)) expand(parentId)
    return result
  }
  const deleteBlogCategory = async (categoryId: string) => {
    const parentId = find(flat, { _id: categoryId })?.parent?._id
    const result = await actions.deleteBlogCategory(categoryId)
    if (isEmpty(result?.errors)) setSelectedId(parentId)
    return result
  }

  const dragDrop = useBlogCategoryDragDrop({ canEdit, onMoveBlog: moveBlog, onMoveBlogCategory: moveBlogCategory })
  const blogCounts = countBlogsByBlogCategory(blogs)
  const allBlogCounts = countBlogsByBlogCategory(allBlogs)
  // A stale selection (a deleted blog category) falls back to the first one.
  const selectedBlogCategory = find(flat, { _id: selectedId }) ?? first(flat)
  const tenantId = tenantFree ? undefined : user?.tenantId

  return (
    <CoreProvider
      value={{
        ...dragDrop,
        tree,
        flat,
        selectedBlogCategory,
        selectedBlogs: filter(blogs, (blog) => blog.category?._id === selectedBlogCategory?._id),
        expandedIds,
        canEdit,
        countBlogsIn: (categoryId) => blogCounts[categoryId] ?? 0,
        countAllBlogsIn: (categoryId) => allBlogCounts[categoryId] ?? 0,
        hasChildren,
        isReserved: (categoryId) => isReservedBlogCategoryId(categoryId, tenantId),
        isWhatsNewSelected: selectedBlogCategory?._id === getReservedBlogCategoryIds(tenantId).whatsNew,
        selectBlogCategory: setSelectedId,
        toggleExpanded: (categoryId) => setExpandedIds((current) => xor(current, [categoryId])),
        moveBlog,
        moveBlogCategory,
        addBlogCategory,
        renameBlogCategory: actions.renameBlogCategory,
        deleteBlogCategory,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useBlogCategoryTreeViewContext = () => useContextCore(context)
